/**
 * Хук для управления состоянием модального окна блока дашборда.
 *
 * Централизует общую логику для AddBlockModal, EditBlockModal:
 * - Инициализация формы и Redux state
 * - Загрузка метрик и группировок при смене периода
 * - Обработка изменений типа отображения и периода
 * - Синхронизация локального состояния с Redux
 * - Очистка состояния при закрытии
 */

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormInstance } from 'antd';
import cloneDeep from 'lodash/cloneDeep';

import { setCookie } from '../../../helpers/utils/cookies';
import { MetricsPaths } from '../../../helpers/utils/enums';
import {
  getDefaultMetricsGroupings,
  normalizeCreatedMetrics,
} from '../../../helpers/utils/functions';
import {
  generateListKeys,
  prepareData,
  prepareGroupsData,
} from '../../../helpers/utils/reports';
import { unApi } from '../../../shared/common/api/endpoints/reports/unApi';
import { normalizeMetricRefs } from '../../../shared/common/components/reports/metrics/utils';
import {
  StaticGroupings,
  staticMetrics,
} from '../../../shared/common/components/reports/CreateMetricsModal';
import { injectRegistrationGroupsNode } from '../../../shared/common/components/reports/modal-settings/config';
import { listKeysJsonUniversal } from '../../../shared/common/constants/metrics';
import { useActions } from '../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../shared/common/hooks/useTypedSelector';
import { defaultHeadingStyles, getFiltersFilterList } from '../components/modal/constants';
import { DashboardBlockData } from '../types';

// ==================== ТИПЫ ====================

export type BlockDisplayType = 'chart' | 'table' | 'number' | 'heading';
export type BlockDateType = 'reg' | 'ad' | 'order' | 'payment' | 'web' | 'visit';

export interface BlockModalMode {
  /** Режим модального окна */
  type: 'add' | 'edit' | 'copy';
  /** ID блока (для edit/copy) */
  blockId?: string;
  /** Исходные данные блока (для edit/copy) */
  initialData?: Partial<DashboardBlockData>;
}

export interface UseBlockModalStateOptions {
  /** Форма Ant Design */
  form: FormInstance;
  /** Режим модального окна */
  mode: BlockModalMode;
  /** Видимость модального окна */
  visible: boolean;
  /** Callback при закрытии модального окна */
  onClose?: () => void;
}

/** Настройки ячеек таблицы, которые хранятся в блоке дашборда */
export type TableCellSettings = Pick<DashboardBlockData, 'sparklines' | 'cellFills' | 'analysis' | 'cellFilling'>;

// ==================== КОНСТАНТЫ ====================

const DATE_TO_PATHNAME: Record<BlockDateType, MetricsPaths> = {
  reg: MetricsPaths.Universal,
  ad: MetricsPaths.AdReports,
  order: MetricsPaths.OrderReports,
  payment: MetricsPaths.PaymentReports,
  web: MetricsPaths.WebinarReports,
  visit: MetricsPaths.Universal,
};

const DATE_SUFFIX_MAP: Partial<Record<BlockDateType, string>> = {
  order: '_2',
  payment: '_3',
  web: '_4',
};

// Типы виджетов, где в дереве группировок показываем узел «Группы регистраций».
// visit исключён — как и в отчётах (там узла групп нет).
const GROUP_NODE_DATE_TYPES: BlockDateType[] = [ 'reg', 'ad', 'order', 'payment', 'web' ];

// ==================== ХУК ====================

export const useBlockModalState = (options: UseBlockModalStateOptions) => {
  const { form, mode, visible } = options;

  // Ref для mode, чтобы useCallback всегда имел актуальные данные
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // ==================== REDUX ====================

  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { details } = useTypedSelector((state) => state.details);
  const { filters, filterParent } = useTypedSelector((state) => state.filter.dashboardModal);
  const { attributes } = useTypedSelector((state) => state.attributes);
  const {
    groupings,
    metrics,
    metricsChecked,
    groupsChecked,
    totalChecked,
    percentages,
    sparklines,
    cellFills,
    analysis,
    cellFilling,
    filtersTable,
    tableData,
    sortTable,
    groupsSelectionMode,
    selectFirstNRows,
  } = useTypedSelector((state) => state.table);
  // «Группы регистраций» всегда глобальные (как и createdMetrics), не блочные
  const customGroups = useTypedSelector((state) => state.table.customGroups);
  const { chartType, isAxesVisible } = useTypedSelector((state) => state.chart);
  const { isCreateMetricsVisible } = useTypedSelector((state) => state.reports);

  const {
    setGroupings,
    setMetrics,
    setMetricsChecked,
    setGroupingsChecked,
    setFilters,
    setFilterParent,
    setAttributes,
    setDetails,
    setChartType,
    setTotalChecked,
    clearTotalChecked,
    setMetaModalTableFetching,
    setMetaModalChartFetching,
    setPercentages,
    setSparklines,
    setCellFills,
    setTableAnalysis,
    setTableCellFilling,
    setTableData,
    setIsNotParamsFilterVisible,
    setGroupsSelectionMode,
    setSelectFirstNRows,
    setAxesVisibleValue,
    setCreatedMetrics,
    getCustomGroups,
  } = useActions();

  /**
   * Настройки ячеек таблицы блока (спарклайны, заливка по условиям, «Динамика», заливка всей таблицы)
   * кладём в общее состояние окна — ReportTable окна и кнопки столбцов работают с ним.
   * Без аргумента — очистка (новый блок / закрытие окна).
   */
  const applyTableCellSettings = useCallback((data?: TableCellSettings) => {
    setSparklines(Array.isArray(data?.sparklines) ? data!.sparklines : []);
    setCellFills(data?.cellFills && typeof data.cellFills === 'object' ? data.cellFills : {});
    setTableAnalysis(data?.analysis === true);
    setTableCellFilling(data?.cellFilling === true);
  }, [ setSparklines, setCellFills, setTableAnalysis, setTableCellFilling ]);

  // ==================== ЛОКАЛЬНОЕ СОСТОЯНИЕ ====================

  const hasInitialData = mode.type === 'edit' || mode.type === 'copy';
  const [ currentSelect, setCurrentSelect ] = useState<string>(
    hasInitialData ? (mode.initialData?.componentType || 'chart') : 'chart'
  );
  const [ currentDateType, setCurrentDateType ] = useState<string>(
    hasInitialData ? (mode.initialData?.date || 'reg') : 'reg'
  );
  const [ selectedMetrics, setSelectedMetrics ] = useState<any[]>(
    hasInitialData ? (mode.initialData?.selectedMetrics || []) : []
  );
  const [ selectedGroupings, setSelectedGroupings ] = useState<any[]>(
    hasInitialData ? (mode.initialData?.selectedGroupings || []) : []
  );
  const [ dataItemsMetrics, setDataItemsMetrics ] = useState<any[]>([]);
  const [ listKeysMetrics, setListKeysMetrics ] = useState<any[]>([]);
  const [ dataItemsGroupings, setDataItemsGroupings ] = useState<any[]>([]);
  const [ listKeysGroupings, setListKeysGroupings ] = useState<any[]>([]);
  const [ heading, setHeading ] = useState(
    hasInitialData ? (mode.initialData?.heading || '') : ''
  );
  const [ headingStyles, setHeadingStyles ] = useState({
    fontSize: hasInitialData
      ? (mode.initialData?.headingStyles?.fontSize || defaultHeadingStyles.fontSize)
      : defaultHeadingStyles.fontSize,
    fontWeight: hasInitialData
      ? (mode.initialData?.headingStyles?.fontWeight || defaultHeadingStyles.fontWeight)
      : defaultHeadingStyles.fontWeight,
    color: hasInitialData
      ? (mode.initialData?.headingStyles?.color || defaultHeadingStyles.color)
      : defaultHeadingStyles.color,
  });
  const [ isBasicMetricsVisible, setIsBasicMetricsVisible ] = useState(false);
  const [ isBasicGroupingsVisible, setIsBasicGroupingsVisible ] = useState(false);
  const [ isBlockVisible, setIsBlockVisible ] = useState(false);

  // Мемоизируем filtersFilter
  const currentDateField = form.getFieldValue('date');
  const filtersFilterList = useMemo(() => getFiltersFilterList(currentDateField), [ currentDateField ]);

  // ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ====================

  const getCreatedMetrics = useCallback(async (pathname: string) => {
    if (!selectedSchool?.id) return [];
    try {
      const res = await unApi.getCustomMetrics(selectedSchool.id, pathname);
      // Кладём созданные метрики в redux (state.table.createdMetrics).
      // В дашборде ReportTable не рендерится, поэтому иначе createdMetrics
      // остаются пустыми и модалки "Рассчитать метрику" (создание/редактирование)
      // не находят кастомные метрики — как в отчётах.
      setCreatedMetrics(res.data);
      return normalizeCreatedMetrics(res.data);
    } catch {
      return [];
    }
  }, [ selectedSchool?.id, setCreatedMetrics ]);

  const getStaticMetricDataItems = useCallback((pathname: string, additionalMetrics: any[] = []) => {
    return prepareData(
      [
        ...staticMetrics[pathname as keyof typeof staticMetrics],
        {
          name: 'Созданные метрики',
          children: additionalMetrics,
        },
      ],
      '',
      [],
      false
    );
  }, []);

  const getStaticMetricDataListKeys = useCallback((pathname: string) => {
    return form.getFieldValue('display') === 'number'
      ? getDefaultMetricsGroupings(form)
      : JSON.parse(listKeysJsonUniversal[pathname as keyof typeof listKeysJsonUniversal]);
  }, [ form ]);

  /**
   * Строит дерево группировок для виджета, встраивая узел «Группы регистраций»
   * и секции разобранных UTM-меток для поддерживаемых типов (reg/ad/order/payment/web).
   * Ось секций несёт суффикс типа виджета. cloneDeep обязателен:
   * injectRegistrationGroupsNode мутирует дерево, а StaticGroupings —
   * импортированные константы, общие с отчётами.
   */
  const buildGroupingsItems = useCallback((
    pathname: MetricsPaths,
    dateType: string,
    groups: any[]
  ) => {
    const shouldInject = GROUP_NODE_DATE_TYPES.includes(dateType as BlockDateType);
    const rawTree = shouldInject
      ? injectRegistrationGroupsNode(cloneDeep(StaticGroupings[pathname]), groups)
      : cloneDeep(StaticGroupings[pathname]);
    const prepared = prepareGroupsData(rawTree);
    return { dataItems: prepared, listKeys: generateListKeys(prepared) };
  }, []);

  // ==================== ОБРАБОТЧИКИ ====================

  /**
   * Обработчик изменения периода (date).
   * @param value - тип периода
   * @param isInitial - при true НЕ устанавливает metricsChecked автоматически
   *   (они будут установлены отдельно из данных блока)
   * @param initialData - данные для инициализации (метрики/группировки)
   */
  const handlePeriodDateChange = useCallback(async (
    value: string,
    isInitial = false,
    initialData?: { selectedMetrics?: any[]; selectedGroupings?: any[] }
  ) => {
    const pathname = DATE_TO_PATHNAME[value as BlockDateType];
    if (!pathname) return;

    setCurrentDateType(value);
    setCookie('dashboardPathname', pathname, { expires: 3000 });
    form.setFieldValue('date', value);

    // Получаем метрики и группировки
    let metricsData: any[];
    let groupingsData: any[];

    if (isInitial) {
      const data = initialData || modeRef.current.initialData;
      metricsData = data?.selectedMetrics || [];
      groupingsData = data?.selectedGroupings || [];
    } else {
      const defaults = getDefaultMetricsGroupings(form);
      metricsData = defaults.metrics;
      groupingsData = defaults.groupings;
    }

    // Загружаем кастомные метрики
    const customMetrics = await getCreatedMetrics(pathname);

    // Подготавливаем данные для селектов
    const newDataItemsMetrics = getStaticMetricDataItems(pathname, customMetrics as []);
    const newListKeysMetrics = getStaticMetricDataListKeys(pathname);
    const { dataItems: newDataItemsGroupings, listKeys: newListKeysGroupings } =
      buildGroupingsItems(pathname, value, customGroups);

    // Обновляем Redux
    setGroupings({ data: groupingsData });
    setMetrics({ data: metricsData });

    // Обновляем локальное состояние
    setSelectedMetrics(metricsData);
    setSelectedGroupings(groupingsData);
    setDataItemsMetrics(newDataItemsMetrics);
    setListKeysMetrics(newListKeysMetrics);
    setDataItemsGroupings(newDataItemsGroupings);
    setListKeysGroupings(newListKeysGroupings);

    // При !isInitial автоматически устанавливаем metricsChecked и триггерим загрузку
    if (!isInitial) {
      const displayValue = form.getFieldValue('display');
      if ((displayValue === 'chart' || displayValue === 'number') && metricsData.length > 0) {
        const metric = metricsData[0];
        setMetricsChecked({
          data: [ {
            dataKey: metric?.dataKey || metric?.key,
            name: metric?.dataKey || metric?.key,
            label: metric?.name || metric?.label,
          } ]
        });
        setTotalChecked();
      }
      setMetaModalTableFetching({ value: true });
    }
  }, [
    form,
    getCreatedMetrics,
    getStaticMetricDataItems,
    getStaticMetricDataListKeys,
    buildGroupingsItems,
    customGroups,
    setGroupings,
    setMetrics,
    setMetricsChecked,
    setTotalChecked,
    setMetaModalTableFetching,
  ]);

  /**
   * Обработчик смены типа отображения (display).
   */
  const handleDisplayTypeChange = useCallback((type: string) => {
    setCookie('chosenDisplay', type, { expires: 300 });
    setCurrentSelect(type);
    form.setFieldValue('display', type);

    const currentDate = form.getFieldValue('date');
    if (currentDate) {
      handlePeriodDateChange(currentDate);
    }
  }, [ form, handlePeriodDateChange ]);

  /**
   * Универсальный обработчик изменения значения в форме.
   */
  const handleValueChange = useCallback((key: string, value: string) => {
    form.setFieldValue(key, value);

    if (key === 'display') {
      handleDisplayTypeChange(value);
    }
    if (key === 'date') {
      handlePeriodDateChange(value);
    }
  }, [ form, handleDisplayTypeChange, handlePeriodDateChange ]);

  /**
   * Обработчик обновления списка метрик/группировок из ModalSettings.
   */
  const handleListChange = useCallback((
    newList: any[],
    fn1: Dispatch<SetStateAction<any[]>>,
    fn2: (newList: any) => void
  ) => {
    fn1(newList);
    fn2({ data: newList });
  }, []);

  /**
   * Обработчик закрытия модального окна.
   */
  const handleCloseModal = useCallback(() => {
    setMetaModalTableFetching({ value: false });
    setFilters({ isModal: true, data: [] });
    setFilterParent({ isModal: true, data: [] });
  }, [ setMetaModalTableFetching, setFilters, setFilterParent ]);

  /**
   * Переключить предпросмотр блока.
   */
  const togglePreview = useCallback(() => {
    setIsBlockVisible((prev) => {
      if (!prev) {
        setMetaModalChartFetching({ value: true });
      }
      return !prev;
    });
  }, [ setMetaModalChartFetching ]);

  // ==================== СИНХРОНИЗАЦИЯ С REDUX ====================

  const prevSelectedMetricsRef = useRef<any[]>([]);
  const prevSelectedGroupingsRef = useRef<any[]>([]);

  useEffect(() => {
    const metricsChanged = JSON.stringify(selectedMetrics) !== JSON.stringify(prevSelectedMetricsRef.current);
    const groupingsChanged = JSON.stringify(selectedGroupings) !== JSON.stringify(prevSelectedGroupingsRef.current);

    if (currentSelect === 'chart') {
      if (metricsChanged) {
        prevSelectedMetricsRef.current = selectedMetrics;
        // Edit: синхронизируем metricsChecked (нормализуем формат {dataKey, name, label})
        // Add/Copy: синхронизируем metrics (как в оригинальном AddBlockModal)
        if (modeRef.current.type === 'edit') {
          setMetricsChecked({ data: normalizeMetricRefs(selectedMetrics) });
        } else {
          setMetrics({ data: selectedMetrics });
        }
      }
    }
    if (currentSelect === 'table') {
      if (metricsChanged) {
        prevSelectedMetricsRef.current = selectedMetrics;
        setMetrics({ data: selectedMetrics });
      }
      if (groupingsChanged) {
        prevSelectedGroupingsRef.current = selectedGroupings;
        setGroupings({ data: selectedGroupings });
      }
    }
  }, [ selectedMetrics, selectedGroupings, currentSelect ]);

  // ==================== ОБНОВЛЕНИЕ МЕТРИК ПОСЛЕ СОЗДАНИЯ КАСТОМНОЙ ====================

  const prevIsCreateMetricsVisibleRef = useRef(isCreateMetricsVisible);

  useEffect(() => {
    const wasVisible = prevIsCreateMetricsVisibleRef.current;
    prevIsCreateMetricsVisibleRef.current = isCreateMetricsVisible;

    // Обнаруживаем закрытие CreateMetricsModal (true → false)
    if (wasVisible && !isCreateMetricsVisible) {
      const currentDate = form.getFieldValue('date');
      const pathname = DATE_TO_PATHNAME[currentDate as BlockDateType];
      if (!pathname) return;

      // Перезагружаем кастомные метрики и обновляем dataItemsMetrics
      getCreatedMetrics(pathname).then((customMetrics) => {
        const newDataItemsMetrics = getStaticMetricDataItems(pathname, customMetrics as []);
        setDataItemsMetrics(newDataItemsMetrics);
      });
    }
  }, [ isCreateMetricsVisible, form, getCreatedMetrics, getStaticMetricDataItems ]);

  // ==================== ГРУППЫ РЕГИСТРАЦИЙ ====================

  // Подгружаем список «Групп регистраций» при открытии модалки (как в ReportGroupings).
  // section_id фиксирован на бэке; pathname уходит только в заголовок/ключ кэша.
  // ВАЖНО: getCustomGroups НЕ в deps — useActions создаёт новую ссылку каждый
  // рендер, и её включение зациклило бы эффект (диспатч → ре-рендер → диспатч).
  useEffect(() => {
    if (visible && selectedSchool?.id) {
      getCustomGroups(selectedSchool.id, window.location.pathname, undefined, false);
    }
  }, [ visible, selectedSchool?.id ]);

  // customGroups приходят асинхронно — пересобираем дерево группировок текущего типа
  // виджета, чтобы узел наполнился данными.
  useEffect(() => {
    const currentDate = form.getFieldValue('date');
    const pathname = DATE_TO_PATHNAME[currentDate as BlockDateType];
    if (!pathname || !GROUP_NODE_DATE_TYPES.includes(currentDate as BlockDateType)) return;
    const { dataItems, listKeys } = buildGroupingsItems(pathname, currentDate, customGroups);
    setDataItemsGroupings(dataItems);
    setListKeysGroupings(listKeys);
  }, [ customGroups ]);

  // ==================== RETURN ====================

  return {
    // Redux state
    details,
    filters,
    filterParent,
    attributes,
    groupings,
    metrics,
    metricsChecked,
    groupsChecked,
    totalChecked,
    percentages,
    sparklines,
    cellFills,
    analysis,
    cellFilling,
    filtersTable,
    tableData,
    sortTable,
    groupsSelectionMode,
    selectFirstNRows,
    chartType,
    isAxesVisible,
    selectedSchool,

    // Локальное состояние
    extraFieldSuffix: DATE_SUFFIX_MAP[currentDateType as BlockDateType],
    currentSelect,
    setCurrentSelect,
    selectedMetrics,
    setSelectedMetrics,
    selectedGroupings,
    setSelectedGroupings,
    dataItemsMetrics,
    dataItemsGroupings,
    listKeysMetrics,
    listKeysGroupings,
    heading,
    setHeading,
    headingStyles,
    setHeadingStyles,
    isBasicMetricsVisible,
    setIsBasicMetricsVisible,
    isBasicGroupingsVisible,
    setIsBasicGroupingsVisible,
    isBlockVisible,
    setIsBlockVisible,
    filtersFilterList,

    // Обработчики
    handlePeriodDateChange,
    handleDisplayTypeChange,
    handleValueChange,
    handleListChange,
    handleCloseModal,
    togglePreview,
    applyTableCellSettings,

    // Redux actions (для уникальной логики модалей)
    actions: {
      setGroupings,
      setMetrics,
      setMetricsChecked,
      setGroupingsChecked,
      setFilters,
      setFilterParent,
      setAttributes,
      setDetails,
      setChartType,
      setTotalChecked,
      clearTotalChecked,
      setMetaModalTableFetching,
      setMetaModalChartFetching,
      setPercentages,
      setTableData,
      setIsNotParamsFilterVisible,
      setGroupsSelectionMode,
      setSelectFirstNRows,
      setAxesVisibleValue,
    },
  };
};

export default useBlockModalState;
